<template>
  <div v-if="loaded" class="container is-fluid" ref="container">
    <section class="section">
      <div class="mb-4 is-flex is-align-items-center has-gap-5">
        <div class="is-flex is-align-items-center">
          <span class="is-size-4 has-text-weight-bold"
            >{{ $t("bnpl:application.title.edit") }}
            {{ bnplApplication.id }}
          </span>

          <span v-if="loaded" class="status-badge ml-4 status-badge-blue">
            <span class="has-text-white">{{
              $t(BNPL_APPLICATION_STATUS_TOKENS[bnplApplication.currentStatus])
            }}</span>
          </span>

          <span
            v-if="loaded"
            class="status-badge ml-4"
            :class="
              bnplApplication.currentStatusResult === 'SUCCESS' ||
              bnplApplication.currentStatusResult === 'CLOSED_APPROVED'
                ? 'status-badge-green'
                : 'status-badge-red'
            "
          >
            <span class="has-text-white">{{
              $t(
                BNPL_APPLICATION_STATUS_RESULT_TOKENS[
                  bnplApplication.currentStatusResult
                ]
              )
            }}</span>
          </span>
        </div>

        <div class="is-size-4 has-text-weight-bold">-</div>

        <div class="is-flex is-align-items-center">
          <span class="is-size-4 has-text-weight-bold"
            >{{ $t("bnpl:deal.title.edit") }} {{ deal.id }}</span
          >
          <span v-if="loaded" class="status-badge ml-4 status-badge-blue">
            <span class="has-text-white">{{
              $t(BNPL_DEAL_STATUS_TOKENS[deal.status])
            }}</span>
          </span>
        </div>

        <div class="ml-auto">
          <b-button
            v-if="refundVisible"
            class="is-size-7 has-text-danger mx-4 is-outlined"
            @click="refundConfirmModal()"
            type="is-danger"
            >{{ $t("bnpl:deal.button.refund") }}
          </b-button>
        </div>
      </div>

      <div class="columns">
        <div class="column is-three-quarters">
          <div class="entity-details-card">
            <div class="is-flex is-flex-direction-column">
              <div class="is-flex is-justify-content-space-between">
                <PartnerLogo :offerId="bnplApplication.offerId" />

                <div>
                  <p class="has-text-weight-bold">
                    {{ bnplApplication.financier }}
                  </p>
                </div>

                <div>
                  <p class="has-text-weight-bold">
                    {{ bnplApplication.offerName }}
                  </p>
                </div>

                <div v-if="isLate" class="mb-3">
                  <BnplTransactionsDelay
                    :due-date="lateSince"
                    :sum="lateSum"
                  ></BnplTransactionsDelay>
                </div>
              </div>

              <div
                style="
                  display: grid;
                  grid-template-columns: repeat(4, minmax(0, 1fr));
                  grid-gap: 12px;
                "
              >
                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.transaction.header.cart.value") }}
                  </p>
                  <span class="has-text-weight-medium">
                    {{
                      localizedFormatter(country.languageIso).money(
                        bnplApplication.currencyIso,
                        bnplApplication.cartValue
                      )
                    }}
                  </span>
                </div>

                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.transaction.header.total.repayable") }}
                  </p>
                  <span class="has-text-weight-medium">{{
                    localizedFormatter(country.languageIso).money(
                      deal.currencyIso,
                      deal.totalRepayable
                    )
                  }}</span>
                </div>

                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("bnpl:application.header.commission.fee.amount") }}
                  </p>
                  <span class="has-text-weight-medium">{{
                    localizedFormatter(country.languageIso).money(
                      deal.currencyIso,
                      deal.commissionValue
                    )
                  }}</span>
                </div>

                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("bnpl:application.header.service.fee.amount") }}
                  </p>
                  <span class="has-text-weight-medium">{{
                    localizedFormatter(country.languageIso).money(
                      deal.currencyIso,
                      deal.serviceFee
                    )
                  }}</span>
                </div>

                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{
                      $t(
                        "bnpl:deal.transaction.paid_per_total_number_of_fragments"
                      )
                    }}
                  </p>
                  <span v-if="loaded" class="has-text-weight-medium"
                    >{{ deal.successfulTransactions }} /
                    {{ deal.transactions.length }}</span
                  >
                </div>

                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("bnpl:application.header.fragments.amount") }}
                  </p>
                  <span class="has-text-weight-medium">{{
                    localizedFormatter(country.languageIso).money(
                      bnplApplication.currencyIso,
                      bnplApplication.transactionSum
                    )
                  }}</span>
                </div>

                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.transaction.amount.received") }}
                  </p>
                  <span class="has-text-weight-medium">{{
                    localizedFormatter(country.languageIso).money(
                      deal.currencyIso,
                      deal.successfulTransactionsAmount
                    )
                  }}</span>
                </div>

                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.header.date.panel.time.of.first.payment") }}
                  </p>
                  <span class="has-text-weight-medium">
                    {{
                      firstTransactionDate
                        ? localizedFormatter(country.languageIso).dateTime(
                            firstTransactionDate
                          )
                        : "-"
                    }}
                  </span>
                </div>
              </div>
              <hr />
              <div
                style="
                  display: grid;
                  grid-template-columns: repeat(3, minmax(0, 1fr));
                  grid-gap: 24px;
                "
              >
                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("bnpl:application.header.date_of_purchase") }}:
                  </p>
                  <span class="has-text-weight-medium">{{
                    localizedFormatter(country.languageIso).dateTime(
                      deal.created
                    )
                  }}</span>
                </div>

                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.header.date.panel.expiry.date") }}
                  </p>
                  <span class="has-text-weight-medium">
                    {{
                      localizedFormatter(country.languageIso).date(dealDueDate)
                    }}</span
                  >
                </div>
                <div>
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.header.date.panel.order.id") }}
                  </p>
                  <span class="has-text-weight-medium">
                    {{ deal.orderId }}</span
                  >
                </div>

                <div v-if="bnplApplication.shoppingMode">
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.header.shopping_mode") }}
                  </p>
                  <span class="has-text-weight-medium">
                    {{
                      BNPL_SHOPPING_MODE_TOKENS[bnplApplication.shoppingMode]
                        ? $t(
                            BNPL_SHOPPING_MODE_TOKENS[
                              bnplApplication.shoppingMode
                            ]
                          )
                        : bnplApplication.shoppingMode
                    }}
                  </span>
                </div>

                <div
                  v-if="
                    bnplApplication.shoppingMode === 'IN_STORE' &&
                    bnplApplication.createdByName
                  "
                >
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.header.created_by_name") }}
                  </p>
                  <span class="has-text-weight-medium">{{
                    bnplApplication.createdByName
                  }}</span>
                </div>

                <div
                  v-if="
                    bnplApplication.shoppingMode === 'IN_STORE' &&
                    bnplApplication.createdByStore
                  "
                >
                  <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                    {{ $t("mp:deal.header.created_by_store") }}
                  </p>
                  <span class="has-text-weight-medium">{{
                    bnplApplication.createdByStore
                  }}</span>
                </div>
              </div>
            </div>
          </div>
          <b-skeleton v-if="!loaded" height="140"></b-skeleton>
        </div>

        <div class="column">
          <div v-if="loaded" class="entity-details-card">
            <div
              class="is-flex is-flex-direction-column is-justify-content-space-between"
              style="flex-grow: 1"
            >
              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("mp:deal.customer.details.company_name") }}
                </p>
                <span
                  class="has-text-weight-medium"
                  v-if="bnplApplication && bnplApplication.companyShortName"
                >
                  {{ bnplApplication.companyShortName }}
                </span>
                <span v-else class="has-text-weight-medium">
                  {{ $t("mp:no_data") }}
                </span>
              </div>
              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("mp:deal.customer.details.name") }}
                </p>
                <span
                  class="has-text-weight-medium"
                  v-if="customer && (customer.firstName || customer.lastName)"
                >
                  {{ customer.lastName }}
                  {{ customer.firstName }}
                </span>
                <span v-else class="has-text-weight-medium">
                  {{ $t("mp:no_data") }}
                </span>
              </div>
              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("mp:deal.customer.details.phone") }}
                </p>
                <span
                  class="has-text-weight-medium"
                  v-if="customer && customer.phone"
                >
                  {{ customer.phone }}
                </span>
                <span v-else> {{ $t("mp:no_data") }} </span>
              </div>
              <div>
                <p class="is-size-7 is-uppercase mb-1 has-text-grey-icon">
                  {{ $t("mp:deal.customer.details.email") }}
                </p>
                <span
                  class="has-text-weight-medium"
                  v-if="customer && customer.emailConfirmed && customer.email"
                >
                  {{ customer.email }}
                </span>
                <span v-else> {{ $t("mp:no_data") }} </span>
              </div>
            </div>
          </div>

          <b-skeleton v-else height="140"></b-skeleton>
        </div>
      </div>
    </section>

    <section class="section">
      <template v-if="loaded">
        <b-tabs v-model="activeTab" class="mb-0 mt-4">
          <b-tab-item
            :value="TABS.BASE_DATA"
            :label="$t('bnpl:application.tab.base_data')"
          />
          <b-tab-item
            :value="TABS.CART"
            :label="$t('bnpl:application.tab.cart')"
          />
          <b-tab-item
            :value="TABS.TRANSACTIONS"
            :label="$t('bnpl:deal.transaction.tab.transactions')"
          />
          <b-tab-item
            :value="TABS.SIGNATURE"
            :label="$t('merchant:merchant_application.tabs.signature')"
          />
        </b-tabs>

        <!-- ALAPADATOK -->
        <template v-if="activeTab === TABS.BASE_DATA">
          <div class="columns">
            <div class="column is-two-thirds">
              <BnplApplicationBaseData
                :raw-data="bnplApplication.dynamicData"
                :flow="flow"
                :application-id="bnplApplication.id"
                :bnpl-view="true"
                :customer="customer"
              />
            </div>
          </div>
        </template>

        <!-- KOSÁR -->
        <template v-if="activeTab === TABS.CART">
          <BnplPurchaseCartTile
            :purchase-id="bnplApplication.purchaseId"
            :currency="bnplApplication.currencyIso"
          ></BnplPurchaseCartTile>
        </template>

        <!-- TRANZAKCIÓK -->
        <template v-if="activeTab === TABS.TRANSACTIONS">
          <BnplTransactionsTile :deal="deal" :transactions="deal.transactions">
          </BnplTransactionsTile>
        </template>

        <!-- SIGNATURE -->
        <template v-if="activeTab === TABS.SIGNATURE">
          <BnplRequiredDocumentsTile
            :bnplApplication="bnplApplication"
            :offerId="bnplApplication.offerId"
            :documents="documents"
          >
          </BnplRequiredDocumentsTile>
        </template>
      </template>
    </section>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { formatDate, formatDateTime, formatMoney } from "@/utils/util";
import {
  BNPL_APPLICATION_STATUS,
  BNPL_APPLICATION_STATUS_RESULT,
  BNPL_APPLICATION_STATUS_RESULT_TOKENS,
  BNPL_APPLICATION_STATUS_TOKENS,
  BNPL_DEAL_STATUS_RESULT,
  BNPL_DEAL_STATUS_TOKENS,
  BNPL_DEAL_TRANSACTION_ATTEMPT_STATUS,
  BNPL_SHOPPING_MODE_TOKENS,
  BNPL_TRANSACTION_STATUS,
  LOAN_APPLICATION_DECISION_MODAL_TYPE,
  NOTE_ENTITY_NAME,
} from "@/utils/const";
import BnplPurchaseCartTile from "@/components/bnpl/application/BnplPurchaseCartTile";
import BnplTransactionsTile from "@/components/deal/BnplTransactionsTile.vue";
import i18n from "@/plugins/i18n";
import BnplTransactionsDelay from "@/components/deal/BnplTransactionDelay.vue";
import BnplRequiredDocumentsTile from "@/views/BnplRequiredDocumentsTile.vue";
import { localizedFormatter } from "../utils/util";

const TABS = {
  BASE_DATA: "baseData",
  UPLOADED_DOCUMENTS: "uploadDocuments",
  STATEMENTS: "statements",
  IDENTIFICATION: "identification",
  DOC_GEN: "offerDocGenerations",
  FINAL_DOCUMENTS: "finalDocuments",
  HISTORY: "history",
  DEAL: "deal",
  CART: "cart",
  SCORING: "scoring",
  CORRECTION: "correction",
  TRANSACTIONS: "transactions",
  SIGNATURE: "signature",
};

export default {
  name: "BnplB2bApplication",
  title: "pp:page.bnpl_application.title",
  components: {
    BnplTransactionsDelay,
    BnplTransactionsTile,
    BnplPurchaseCartTile,
    BnplRequiredDocumentsTile,
    PartnerLogo: () => import("@/components/PartnerLogo"),
    BnplApplicationBaseData: () =>
      import("@/components/bnpl/application/BnplApplicationBaseData"),
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      TABS,
      BNPL_APPLICATION_STATUS,
      BNPL_APPLICATION_STATUS_TOKENS,
      BNPL_APPLICATION_STATUS_RESULT,
      BNPL_APPLICATION_STATUS_RESULT_TOKENS,
      BNPL_SHOPPING_MODE_TOKENS,
      LOAN_APPLICATION_DECISION_MODAL_TYPE,
      NOTE_ENTITY_NAME,
      activeTab: TABS.DEAL,
      bnplApplication: {},
      flow: {},
      deal: {},
      documents: {},
      bnplApplicationBaseData: {}, // actual calculations
      customer: {},
      loaded: false,
    };
  },
  computed: {
    ...mapGetters({
      country: "i18n/country",
    }),
    BNPL_DEAL_STATUS_TOKENS() {
      return BNPL_DEAL_STATUS_TOKENS;
    },
    BNPL_DEAL_STATUS_RESULT() {
      return BNPL_DEAL_STATUS_RESULT;
    },
    ...mapGetters({
      userId: "session/userId",
      bnplApplicationGetter: "bnplApplicationStore/get",
      getDeal: "deal/get",
    }),
    approveVisible() {
      return (
        this.loaded &&
        this.bnplApplication.currentStatus !== "CLOSED" &&
        this.bnplApplication.currentStatus !== "BANK_RESULT" &&
        this.bnplApplication.currentStatus !== "PURCHASE_BANK_RESULT" &&
        this.bnplApplication.currentStatus !== "COMPLETED"
      );
    },
    editVisible() {
      return (
        this.loaded &&
        this.bnplApplication.currentStatus !== "CLOSED" &&
        this.bnplApplication.currentStatus !== "COMPLETED"
      );
    },
    approveDisabled() {
      return (
        this.loaded &&
        this.bnplApplication.currentStatus !== "UNDER_REVIEW" &&
        this.bnplApplication.currentStatus !== "OFFLINE_UNDER_REVIEW"
      );
    },
    inApprovalPeriod() {
      return (
        this.bnplApplication.currentStatus === BNPL_APPLICATION_STATUS.SIGNED ||
        this.bnplApplication.currentStatus === BNPL_APPLICATION_STATUS.COMPLETED
      );
    },
    refundVisible() {
      return this.loaded && this.deal.status !== "CANCELLED";
    },
    isLate() {
      return this.deal?.transactions?.some((it) => it.isLate);
    },
    lateSum() {
      return this.deal?.transactions
        ?.filter((it) => it.isLate)
        .reduce((acc, it) => acc + it.sum, 0);
    },
    lateSince() {
      if (!this.isLate) return null;
      return this.deal?.transactions?.filter((it) => it.isLate)[0].dueDate;
    },
    firstTransactionDate() {
      if (!!this.deal?.transactions) {
        const firstTransaction = this.deal.transactions.find(
          (it) => it.seq === 1
        );
        if (!!firstTransaction?.attempts) {
          const successfulAttempt = firstTransaction.attempts.find(
            (it) =>
              it.result === BNPL_DEAL_TRANSACTION_ATTEMPT_STATUS.ENUM.SUCCESS
          );
          if (!!successfulAttempt) {
            return formatDate(successfulAttempt.timestamp);
          }
        }
        return null;
      }
      return null;
    },
    dealDueDate() {
      if (!!this.deal?.transactions) {
        return formatDate(this.deal.transactions.slice(-1)[0].dueDate);
      }
      return "-";
    },
  },
  async created() {
    await this.fetch();
    this.loaded = true;
  },
  methods: {
    formatDateTime,
    formatMoney,
    formatDate,
    localizedFormatter,
    ...mapActions({
      fetchDeal: "deal/fetch",
    }),
    async fetch() {
      // Fetch and parse logged in user and roles
      await this.$store.dispatch("bnplApplication/fetch", this.id);
      this.bnplApplication = this.$store.getters["bnplApplication/get"](
        this.id
      );
      await this.$store.dispatch(
        "bnplApplication/fetchFlow",
        this.bnplApplication.flowId
      );
      this.flow = this.$store.getters["bnplApplication/getFlow"](
        this.bnplApplication.flowId
      );

      // Fetch customer
      if (!!this.bnplApplication.customerId) {
        await this.$store.dispatch(
          "customer/fetchOne",
          this.bnplApplication.customerId
        );
        this.customer = this.$store.getters["customer/get"](
          this.bnplApplication.customerId
        );
      }

      //Fetch deal
      await this.$store.dispatch("deal/fetch", this.bnplApplication.dealId);
      this.deal = this.getDeal(this.bnplApplication.dealId);
      this.deal.transactions.sort(
        (a, b) => new Date(a.dueDate) - new Date(b.dueDate)
      );
      this.deal.totalRepayable = this.deal.transactions.reduce(
        (a, b) => +a + +b.sum,
        0
      );

      this.bnplApplication.transactionSum = this.deal.transactions.find(
        (tr) => tr.seq === 2
      )?.sum;

      this.bnplApplication.totalRepayable = this.deal.transactions.reduce(
        (a, b) => +a + +b.sum,
        0
      );

      let successfulTransactions = 0;
      let successfulTransactionsAmount = 0;
      this.deal.transactions.forEach((value) => {
        if (value.status === BNPL_TRANSACTION_STATUS.ENUM.PAID) {
          ++successfulTransactions;
          successfulTransactionsAmount += value.sum;
        }
      });
      this.deal.successfulTransactions = successfulTransactions;
      this.deal.successfulTransactionsAmount = successfulTransactionsAmount;

      await this.$store.dispatch(
        "bnplApplication/fetchDocuments",
        this.bnplApplication.id
      );
      this.documents = this.$store.getters["bnplApplication/getDocuments"](
        this.bnplApplication.id
      );

      this.activeTab = TABS.BASE_DATA;
    },

    refundConfirmModal() {
      this.$buefy.dialog.confirm({
        title: i18n.t("bnpl:deal.delete_confirm.button.title"),
        message: i18n.t("bnpl:deal.delete_confirm.button.message"),
        cancelText: i18n.t("bnpl:deal.delete_confirm.button.cancel"),
        confirmText: i18n.t("bnpl:deal.delete_confirm.button.confirm"),
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.refundDeal(),
      });
    },
    async refundDeal() {
      await this.$store.dispatch("deal/refund", this.bnplApplication.id);
      await this.$router.push("/deals");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/colors.scss";

.entity-details-card {
  padding: 1.5rem;
  margin-bottom: 1rem;
  .partner-logo {
    height: 25px;
  }
}

.tile-empty {
  background: $grey-border;
  border-radius: 8px;
}

.has-text-danger:focus,
.has-text-danger:hover {
  color: white !important;
}

.has-text-danger:focus[disabled],
.has-text-danger:hover[disabled] {
  color: #f14668 !important;
}

.tile {
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  align-items: center;
}
</style>
